/**
 * A Wedding Books KPI tile: eyebrow, headline number, sub-line, delta, and an
 * optional sparkline.
 *
 * WHY THIS IS NOT `common/MetricCard`. That card has twenty-odd consumers and
 * three properties this screen cannot use:
 *   · it abbreviates numeric values through `formatNumber` (1,847 becomes
 *     "2K"), and these tiles show exact counts;
 *   · its trend chip hardcodes a `%` suffix, so it cannot render "+9 books" or
 *     the "new" state a null delta requires;
 *   · it has no slot for a sparkline.
 * Bending MetricCard to fit would have meant three new optional props and a
 * behaviour change inside a component every other screen depends on. This is a
 * wedding-local composition instead: nothing outside this section can break.
 *
 * THE DELTA IS ALLOWED TO BE ABSENT. The server sends `delta_pct: null` when
 * the previous period was zero, because "up 100% from nothing" is not a
 * measurement. That renders as a neutral "new" chip, never as a percentage.
 *
 * ZERO HEX. Every colour is a MUI palette key resolved from the live theme.
 */

import React from 'react';
import { Box, Card, CardContent, Chip, Skeleton, Tooltip, Typography } from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import Sparkline, { SparklineTone } from './Sparkline';
import type { KpiSeriesPoint } from './weddingApi';

export interface WeddingKpiCardProps {
  /** Small uppercase label, e.g. "Revenue · 30 days". */
  title: string;
  /** Already formatted — this card never does arithmetic or abbreviation. */
  value: React.ReactNode;
  subtitle?: React.ReactNode;
  /** Percentage change against the prior period, or null for "new". */
  deltaPct?: number | null;
  /** Rendered instead of a percentage when the caller has a better unit. */
  deltaLabel?: string;
  /** Set false to hide the delta entirely (no prior period to compare with). */
  showDelta?: boolean;
  series?: KpiSeriesPoint[] | null;
  tone?: SparklineTone;
  loading?: boolean;
  /** Makes the whole tile a button — used by the at-risk filter on Books. */
  onClick?: () => void;
  /** Paints the headline number in a palette colour (at-risk = 'error.main'). */
  valueColor?: string;
  tooltip?: string;
  'data-testid'?: string;
}

const WeddingKpiCard: React.FC<WeddingKpiCardProps> = ({
  title,
  value,
  subtitle,
  deltaPct,
  deltaLabel,
  showDelta = true,
  series,
  tone = 'primary',
  loading = false,
  onClick,
  valueColor,
  tooltip,
  'data-testid': testId,
}) => {
  const hasDelta = showDelta && (deltaLabel !== undefined || deltaPct !== undefined);
  const up = typeof deltaPct === 'number' && deltaPct > 0;
  const down = typeof deltaPct === 'number' && deltaPct < 0;

  const deltaChip = (() => {
    if (!hasDelta) return null;
    if (deltaLabel !== undefined) {
      return <Chip size="small" variant="outlined" label={deltaLabel} />;
    }
    if (deltaPct === null || deltaPct === undefined) {
      return (
        <Tooltip title="Nothing happened in the previous period, so there is no percentage to compare against.">
          <Chip size="small" variant="outlined" label="new" />
        </Tooltip>
      );
    }
    return (
      <Chip
        size="small"
        variant="outlined"
        color={up ? 'success' : down ? 'error' : 'default'}
        icon={
          up ? <ArrowUpwardIcon fontSize="small" />
            : down ? <ArrowDownwardIcon fontSize="small" />
              : undefined
        }
        label={`${deltaPct > 0 ? '+' : ''}${deltaPct}%`}
      />
    );
  })();

  const body = (
    <CardContent sx={{ pb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700 }}
        >
          {title}
        </Typography>
        {deltaChip}
      </Box>

      {loading ? (
        <Skeleton variant="text" width="55%" height={44} />
      ) : (
        <Typography
          variant="h4"
          sx={{ fontWeight: 700, lineHeight: 1.2, mt: 0.5, color: valueColor || 'text.primary' }}
        >
          {value}
        </Typography>
      )}

      {subtitle !== undefined && subtitle !== null && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {subtitle}
        </Typography>
      )}

      {!loading && <Sparkline points={series} tone={tone} label={`${title} trend`} />}
    </CardContent>
  );

  const card = (
    <Card
      data-testid={testId}
      sx={{
        borderRadius: '16px',
        height: '100%',
        ...(onClick
          ? {
              cursor: 'pointer',
              transition: 'box-shadow 150ms ease-in-out',
              '&:hover': { boxShadow: 4 },
            }
          : {}),
      }}
      {...(onClick
        ? {
            role: 'button',
            tabIndex: 0,
            onClick,
            onKeyDown: (e: React.KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
              }
            },
          }
        : {})}
    >
      {body}
    </Card>
  );

  /*
   * ALWAYS wrapped, even with no tooltip text.
   *
   * Branching on `tooltip` changes the element TYPE at the top of this subtree,
   * so a card whose tooltip only becomes known once the data arrives (the
   * coordinator tile, whose help text is the server's "not in production"
   * reason) would unmount and remount the whole card mid-load — a visible
   * flicker, a lost focus ring, and a detached node for anything holding a
   * reference to it. MUI renders no tooltip for an empty title.
   */
  return <Tooltip title={tooltip || ''}>{card}</Tooltip>;
};

export default WeddingKpiCard;
